import { Component, h, Prop } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../utils/helpers/hydration';

/**
 * How it works component that displays step-by-step instructions
 * with icons and descriptions.
 *
 * @product signnow
 * @source hiw
 * @category Features
 * @constant HOW_IT_WORKS_HEADING - "How it works" - Section heading
 * @example
 *   <sn-how-it-works
 *     slot="sidebar"
 *     .constants='{"HOW_IT_WORKS_HEADING": "How it works"}'
 *     .values='{"items": [{"title": "Open up", "description": "a PDF file in the editor"}]}'
 *   >
 *   </sn-how-it-works>
 */
@Component({
  tag: 'sn-how-it-works',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHowItWorks {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * @dynamic
   * @type {object}
   * @property {array} items - How it works items
   */
  @Prop({ mutable: true }) values?:
    | {
        items?: Array<{
          title: string;
          description: string;
        }>;
      }
    | string;

  /**
   * Array of how it works items to be displayed.
   * Icons are automatically assigned based on item index (3 unique icons).
   * @dynamic
   * @type {array}
   * @items.type {object}
   * @items.property {string} title - Step title
   * @items.property {string} description - Step description
   * @items.required title
   * @items.required description
   * @minItems 1
   * @maxItems 3
   */
  @Prop({ mutable: true }) items?:
    | Array<{
        title: string;
        description: string;
      }>
    | string;

  private sectionTitle?: string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);

    // Fallback: if items is not provided, try to get from values
    if (!this.items && this.values && typeof this.values !== 'string' && this.values.items) {
      this.items = this.values.items;
    }

    // Ensure items is an array
    if (!Array.isArray(this.items)) {
      this.items = [];
    }

    // Parse description from first item if it contains newlines
    const rawItems = this.items;
    const parsedItems: Array<{ title: string; description: string }> = [];

    if (rawItems.length > 0 && rawItems[0].description && rawItems[0].description.includes('\n')) {
      // Store section title from the first item's title field
      this.sectionTitle = rawItems[0].title || undefined;

      // Split description by newlines and parse each line
      const lines = rawItems[0].description.split('\n').filter((line: string) => line.trim());

      lines.forEach((line: string) => {
        // Find first colon and split by it
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          let title = line.substring(0, colonIndex).trim();
          const description = line.substring(colonIndex + 1).trim();

          // Remove leading numbers like "1. ", "2. ", "3. " etc from title
          title = title.replace(/^\d+\.\s*/, '');

          parsedItems.push({ title, description });
        } else {
          // If no colon, use entire line as description
          parsedItems.push({ title: '', description: line.trim() });
        }
      });

      this.items = parsedItems;
    }
  }

  getSvgIcon(index: number) {
    const icons = [
      // Icon 1 - Open up (blue)
      <me-icon name="https://marketing-static.signnow.com/347/images/me/how-it-works/icon-1.svg" />,
      // Icon 2 - Draw (green)
      <me-icon name="https://marketing-static.signnow.com/347/images/me/how-it-works/icon-2.svg" />,
      // Icon 3 - Download (red/orange)
      <me-icon name="https://marketing-static.signnow.com/347/images/me/how-it-works/icon-3.svg" />,
    ];
    return icons[index % icons.length];
  }

  render() {
    // Ensure items is parsed and is an array
    if (!Array.isArray(this.items) || this.items.length === 0) {
      return null;
    }

    // Get heading: use sectionTitle if available, otherwise from constants
    const heading = this.sectionTitle || getConstant(this.constants, 'HOW_IT_WORKS_HEADING', 'How it works');

    return (
      <div class="column">
        <h4 class="section-side-title">{heading}</h4>
        <div class="how-it-works">
          {this.items.map((item, index) => (
            <div class="how-it-works__item" key={index}>
              <div class="how-it-works__icon">{this.getSvgIcon(index)}</div>
              <div class="how-it-works__details">
                {item.title && <strong>{item.title}</strong>}
                {item.title && ' '}
                {item.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
}
